import React from 'react';
import { Grid, Typography } from '@mui/material';
import { blue, green, orange } from '@mui/material/colors';
import { Translate } from 'react-localize-redux';
import { ButtonContinue } from '../mini_components';
import { canOfferNoKnown } from '../ClinicalSummary/allergyState';
import type { AllergyState } from '../ClinicalSummary/allergyState';

export interface AllergyFirstStepProps {
    /**
     * SHER-1057: the patient's current allergy state (Clinical Summary). When
     * omitted the step keeps its SHER-263 P6 behaviour (both actions), so hosts
     * that do not know the state are unchanged. Null = the host is still reading
     * it: only "Add new allergy" until it is known.
     */
    allergyState?: AllergyState | null;
    /** Open the add-allergy form (active-principle autocomplete; carries its own Cancel). */
    onAddNew: () => void;
    /** Record "reviewed, no known allergies" (the NAMC sentinel / green state). */
    onNoKnown: () => void;
    /** SHER-1057: open the pre-filled "Update allergies" surface. No handler → no button. */
    onUpdate?: () => void;
}

/**
 * SHER-1057 — the three actions must be visually distinct. These are Pedro's
 * proposed colours (orange add / blue update / green no-known); the final call
 * is a presentation decision for the product owner, so they live here, once.
 */
export const ALLERGY_ACTION_COLOURS = {
    addNew: orange[800],
    update: blue[600],
    noKnown: green[600],
};

/**
 * SHER-263 P6 — allergy form first step: explicit actions instead of the old
 * "Allergies: Yes / No" dropdown.
 *
 * SHER-1057 — the actions follow the patient's allergy state, so "documented
 * allergies" and "no known allergies" (mutually exclusive) cannot be swapped by
 * accident, and the update path is reachable from the form, not only the banner:
 *   - not documented → "Add new allergy" + "Mark as no known allergies".
 *   - known          → the current allergies + "Update allergies" + "Add new allergy";
 *                      never "no known" (it appears once every allergy is removed,
 *                      inside "Update allergies").
 *   - no known       → the "No known allergies" status + "Add new allergy".
 *
 * Presentational only: the host (SmartField) owns the state transitions.
 */
function AllergyFirstStep({ allergyState, onAddNew, onNoKnown, onUpdate }: AllergyFirstStepProps) {
    const status = allergyState?.status;
    const offerNoKnown = canOfferNoKnown(allergyState, 0);

    return (
        <Grid container spacing={1} style={{ paddingTop: '1rem' }} data-testid="allergy-first-step">
            {status === 'known' && (
                <Grid item xs={12}>
                    <Typography variant="body1" data-testid="allergy-current">
                        <b><Translate id="clinicalSummary.allergy.label" />:</b> {allergyState!.values.join(', ')}
                    </Typography>
                </Grid>
            )}
            {status === 'noKnown' && (
                <Grid item xs={12}>
                    <Typography variant="body1" data-testid="allergy-status-no-known">
                        <Translate id="clinicalSummary.allergy.noKnown" />
                    </Typography>
                </Grid>
            )}
            {status === 'known' && onUpdate && (
                <Grid item>
                    <ButtonContinue data-testid="allergy-update" onClick={onUpdate} colorRGB={ALLERGY_ACTION_COLOURS.update}>
                        <Translate id="clinicalSummary.allergy.update.title" />
                    </ButtonContinue>
                </Grid>
            )}
            <Grid item>
                <ButtonContinue data-testid="allergy-add-new" onClick={onAddNew} colorRGB={ALLERGY_ACTION_COLOURS.addNew}>
                    <Translate id="clinicalSummary.allergy.addNew" />
                </ButtonContinue>
            </Grid>
            {offerNoKnown && (
                <Grid item>
                    <ButtonContinue data-testid="allergy-mark-no-known" onClick={onNoKnown} colorRGB={ALLERGY_ACTION_COLOURS.noKnown}>
                        <Translate id="clinicalSummary.allergy.update.noKnownToggle" />
                    </ButtonContinue>
                </Grid>
            )}
        </Grid>
    );
}

export default AllergyFirstStep;
